import styled from "styled-components"
import * as PropTypes from "prop-types"
import { useT } from "react-polyglot-hooks"
import { printDate } from "helpers/formatter"
import { sendEvent } from "scripts/GoogleAnalytics"
import LazyLoad from "react-lazyload"
import { color, mq } from "helpers/styled"
import { memo } from "react"
import Typography from "components/styled/Typography"

const Wrapper = styled.div`
  flex: 0 0 100%;

  @media ${mq("lg")} {
    padding: 0 5px;
    flex-basis: ${100 / 3}%;
  }

  @media ${mq("xl")} {
    flex-basis: ${100 / 4}%;
  }
`

const Box = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background-color: ${color("white")};

  @media ${mq("lg")} {
    border-radius: 10px;
    box-shadow: 0 2px 10px 0 ${color("lightGray")};
  }
`

const Image = styled.img`
  width: 100%;
  object-fit: cover;
  height: 220px;
`

const Content = styled.div`
  padding: 15px;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const CardLink = styled(Typography)`
  margin-top: auto;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`

const getImage = (url, w, h) => {
  if (!url) return null

  return url.replace(
    /.*wp-content\/uploads\//,
    `${process.env.NEXT_PUBLIC_CLOUDINARY}f_auto,w_${w},h_${h},c_fill/blog/`
  )
}

function PostCard({ post, page, showDate }) {
  const t = useT()

  const { title, excerpt, _embedded, date, link } = post
  const media = _embedded && _embedded["wp:featuredmedia"] && _embedded["wp:featuredmedia"][0]

  if (!media) {
    console.warn(`Article without media: ${title.rendered}`)
    return null
  }

  const href = link.replace(/.*.com\//, "https://swipcar.com/")

  const handleClick = e => {
    if (!e.defaultPrevented) sendEvent("Click Blog post", { event_category: page, event_label: title.rendered })
  }

  return (
    <Wrapper>
      <Box>
        <LazyLoad height={220}>
          <picture>
            <source srcSet={getImage(media.source_url, 300, 220)} media={mq("lg")} />
            <source srcSet={getImage(media.source_url, 940, 540)} media={mq("sm")} />
            <Image src={getImage(media.source_url, 575, 320)} alt={media.title?.rendered} />
          </picture>
        </LazyLoad>
        <Content>
          {showDate && (
            <Typography variant="small" weight="light" style={{ marginBottom: "25px" }}>
              {printDate(date)}
            </Typography>
          )}
          <Typography
            el="h3"
            variant="title4"
            weight="medium"
            style={{ marginBottom: "15px" }}
            dangerouslySetInnerHTML={{ __html: title.rendered }}
          />
          <Typography variant="small" weight="light" dangerouslySetInnerHTML={{ __html: excerpt.rendered }} />
          <CardLink
            el="a"
            variant="small"
            weight="light"
            color="deepPink"
            href={href}
            target="_blank"
            onClick={handleClick}>
            {t("common.read_more")}
          </CardLink>
        </Content>
      </Box>
    </Wrapper>
  )
}

PostCard.defaultProps = {
  showDate: true
}

PostCard.propTypes = {
  post: PropTypes.object.isRequired,
  page: PropTypes.string.isRequired,
  showDate: PropTypes.bool
}

export default memo(PostCard)
